import React, { useState, useCallback, useEffect } from "react";
import {
  Box,
  Button,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  CircularProgress,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { toast } from "react-toastify";
import FilterListIcon from "@mui/icons-material/FilterList";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { getVendorStats, getVendors, exportVendors, formatApiDate, formatDisplayDateTime, locationFilterParams } from "../../api/superAdminApi";
import { VM_venueTypes } from "../../Helper/RegistrationHelper";
import { useLocationCascading } from "./hooks/useLocationCascading";
import { useAdminPanel } from "./hooks/useAdminPanel";
import SuperAdminLocationSelects from "./SuperAdminLocationSelects";
import SuperAdminPageHeader from "./SuperAdminPageHeader";
import SuperAdminViewAction from "./SuperAdminViewAction";

const ALL = { value: "", label: "All" };
const VENDOR_TYPE_OPTIONS = [
  ALL,
  { value: "decor", label: "Decor" },
  { value: "catering", label: "Catering" },
  { value: "service", label: "Service" },
];
const VENUE_TYPE_OPTIONS = [ALL, ...VM_venueTypes.map((v) => ({ value: v.value, label: v.label }))];

const ROWS_PER_PAGE_OPTIONS = [10, 25, 50];
const FILTER_ITEM = { xs: 12, sm: 6, md: 4, lg: 3 };
const filterFormControlSx = { minWidth: { xs: "100%", sm: 220 }, width: "100%" };
const filterMenuProps = {
  PaperProps: { sx: { maxHeight: 320, minWidth: 240, mt: 0.5 } },
};

function FilterSelect({ label, labelId, value, onChange, options }) {
  return (
    <FormControl fullWidth size="small" sx={filterFormControlSx}>
      <InputLabel id={labelId} shrink>
        {label}
      </InputLabel>
      <Select
        labelId={labelId}
        label={label}
        value={value}
        onChange={onChange}
        displayEmpty
        notched
        MenuProps={filterMenuProps}
      >
        {options.map((o) => (
          <MenuItem key={o.value || "all"} value={o.value}>
            {o.value ? o.label : <em>{o.label}</em>}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

function FilterSectionTitle({ children }) {
  return (
    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "text.secondary" }}>
      {children}
    </Typography>
  );
}

const TABLE_HEADERS = [
  "ID",
  "Vendor name",
  "Mobile",
  "Vendor type",
  "Company name",
  "City",
  "Enquiry ID",
  "Venue type",
  "Created at",
  "Action",
];
const TABLE_COL_SPAN = TABLE_HEADERS.length;

export default function VMSuperAdminVendors() {
  const { adminScope, locationLocked } = useAdminPanel();
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [searchKey, setSearchKey] = useState("");
  const [vendorType, setVendorType] = useState("");
  const [venueType, setVenueType] = useState("");
  const [totalEventVendors, setTotalEventVendors] = useState(0);
  const [rows, setRows] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const loc = useLocationCascading();

  const buildFilters = useCallback(
    (pageOverride) => {
      const params = {
        page: pageOverride !== undefined ? pageOverride : page,
        no_of_rows: rowsPerPage,
        ...locationFilterParams(loc, adminScope, { locked: locationLocked }),
      };
      const from = formatApiDate(fromDate);
      const to = formatApiDate(toDate);
      if (from) params.from = from;
      if (to) params.to = to;
      if (searchKey.trim()) params.search = searchKey.trim();
      if (vendorType) params.vendor_type = vendorType;
      if (venueType) params.venue_type = venueType;
      return params;
    },
    [page, rowsPerPage, fromDate, toDate, searchKey, vendorType, venueType, loc, adminScope, locationLocked]
  );

  const loadData = useCallback(
    async (pageOverride) => {
      setLoading(true);
      const filters = buildFilters(pageOverride);
      try {
        const [statsRes, listRes] = await Promise.all([getVendorStats(filters), getVendors(filters)]);
        setTotalEventVendors(statsRes.data?.data?.total ?? 0);
        setRows(listRes.data?.data || []);
        setTotalRecords(listRes.data?.totalCount ?? 0);
      } catch {
        setTotalEventVendors(0);
        setRows([]);
        setTotalRecords(0);
        toast.info("Vendors API unavailable.");
      } finally {
        setLoading(false);
      }
    },
    [buildFilters]
  );

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearchClick = () => {
    setFiltersOpen(false);
    setPage(0);
    loadData(0);
  };

  const handlePageChange = (_, newPage) => {
    setPage(newPage);
    loadData(newPage);
  };

  const handleRowsPerPageChange = (e) => {
    const next = parseInt(e.target.value, 10);
    setRowsPerPage(next);
    setPage(0);
    setTimeout(() => loadData(0), 0);
  };

  const exportData = async () => {
    try {
      await exportVendors(buildFilters());
    } catch {
      toast.warning("Export failed.");
    }
  };

  const handleReset = () => {
    setFromDate(null);
    setToDate(null);
    setSearchKey("");
    setVendorType("");
    setVenueType("");
    setPage(0);
    if (!locationLocked) {
      loc.resetLocation();
    }
    window.setTimeout(() => loadData(0), 0);
  };

  return (
    <Box>
      <SuperAdminPageHeader
        title="Event Vendors"
        actions={
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" startIcon={<RestartAltIcon />} onClick={handleReset} disabled={loading}>
              Reset
            </Button>
            <Button
              variant={filtersOpen ? "contained" : "outlined"}
              startIcon={<FilterListIcon />}
              onClick={() => setFiltersOpen((o) => !o)}
            >
              Filters
            </Button>
          </Stack>
        }
      />

      {filtersOpen ? (
        <Paper variant="outlined" sx={{ p: 2.5, mb: 2 }}>
          <Stack spacing={2.5}>
            <FilterSectionTitle>Date range</FilterSectionTitle>
            <Grid container spacing={2}>
              <Grid item {...FILTER_ITEM}>
                <DatePicker
                  label="Created from"
                  value={fromDate}
                  onChange={setFromDate}
                  format="DD/MM/YYYY"
                  slotProps={{ textField: { size: "small", fullWidth: true } }}
                />
              </Grid>
              <Grid item {...FILTER_ITEM}>
                <DatePicker
                  label="Created to"
                  value={toDate}
                  onChange={setToDate}
                  format="DD/MM/YYYY"
                  minDate={fromDate?.isValid() ? fromDate : undefined}
                  slotProps={{ textField: { size: "small", fullWidth: true } }}
                />
              </Grid>
            </Grid>

            <Divider />

            <FilterSectionTitle>Location</FilterSectionTitle>
            <Grid container spacing={2}>
              <SuperAdminLocationSelects
                {...loc}
                locationLocked={locationLocked}
                lockedScope={adminScope}
                menuMinWidth={280}
                itemProps={FILTER_ITEM}
              />
            </Grid>

            <Divider />

            <FilterSectionTitle>Vendor filters</FilterSectionTitle>
            <Grid container spacing={2}>
              <Grid item {...FILTER_ITEM}>
                <FilterSelect
                  labelId="vendors-filter-vendor-type"
                  label="Vendor type"
                  value={vendorType}
                  onChange={(e) => setVendorType(e.target.value)}
                  options={VENDOR_TYPE_OPTIONS}
                />
              </Grid>
              <Grid item {...FILTER_ITEM}>
                <FilterSelect
                  labelId="vendors-filter-venue-type"
                  label="Venue type"
                  value={venueType}
                  onChange={(e) => setVenueType(e.target.value)}
                  options={VENUE_TYPE_OPTIONS}
                />
              </Grid>
            </Grid>

            <Divider />

            <FilterSectionTitle>Search</FilterSectionTitle>
            <Grid container spacing={2} alignItems="flex-end">
              <Grid item xs={12} lg={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Search"
                  placeholder="Vendor name, mobile, company, or enquiry ID"
                  value={searchKey}
                  onChange={(e) => setSearchKey(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSearchClick()}
                />
              </Grid>
              <Grid item xs={12} sm={6} lg={3}>
                <Button variant="contained" onClick={handleSearchClick} disabled={loading} fullWidth>
                  {loading ? <CircularProgress size={22} color="inherit" /> : "Search"}
                </Button>
              </Grid>
              <Grid item xs={12} sm={6} lg={3}>
                <Button variant="outlined" onClick={exportData} disabled={loading} fullWidth>
                  Export Data
                </Button>
              </Grid>
            </Grid>
          </Stack>
        </Paper>
      ) : null}

      <Paper sx={{ p: 2, mb: 2, bgcolor: "primary.main", color: "primary.contrastText", maxWidth: 280 }}>
        <Typography variant="body2">Total Event Vendors</Typography>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>{loading ? "…" : totalEventVendors}</Typography>
      </Paper>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: "primary.main" }}>
              {TABLE_HEADERS.map((h) => (
                <TableCell key={h} sx={{ color: "primary.contrastText", fontWeight: 700 }}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={TABLE_COL_SPAN} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={28} aria-label="Loading vendors" />
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={TABLE_COL_SPAN} align="center" sx={{ py: 4, color: "text.secondary" }}>No vendors for the current criteria.</TableCell>
              </TableRow>
            ) : (
              rows.map((row, idx) => (
                <TableRow key={row.id ?? idx}>
                  <TableCell>{page * rowsPerPage + idx + 1}</TableCell>
                  <TableCell>{row.vendor_name ?? row.name ?? "—"}</TableCell>
                  <TableCell>{row.phone_no ?? row.phone ?? row.mobile ?? "—"}</TableCell>
                  <TableCell>{row.vendor_type ?? "—"}</TableCell>
                  <TableCell>{row.company_name ?? row.company ?? "—"}</TableCell>
                  <TableCell>{row.city_name ?? "—"}</TableCell>
                  <TableCell>{row.enquiry_id ?? "—"}</TableCell>
                  <TableCell>{row.venue_type ?? "—"}</TableCell>
                  <TableCell>{formatDisplayDateTime(row.created_at ?? row.createdAt)}</TableCell>
                  <TableCell align="center">
                    <SuperAdminViewAction disabled />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TablePagination rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS} count={totalRecords} rowsPerPage={rowsPerPage} page={page} onPageChange={handlePageChange} onRowsPerPageChange={handleRowsPerPageChange} disabled={loading} colSpan={TABLE_COL_SPAN} sx={{ border: 0 }} />
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>
    </Box>
  );
}
